import React, { useEffect, useState } from "react";
import { Check, Crown, Sparkles, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/pookieApi";
import { useSubscription, getDeviceId, getProEmail, setProEmail } from "@/lib/subscription";
import { openRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";

const FREE_BULLETS = [
  "2 free AI verdicts (forever, on the house)",
  "Browse the public archive",
  "Read featured cases & stats",
];
const PRO_BULLETS = [
  "Unlimited Judge Pookie verdicts",
  "Priority deliberation (still fast tho)",
  "Share verdict cards everywhere",
  "Early access to new categories",
  "Cancel anytime, no drama",
];

export default function PricingPage() {
  const [plan, setPlan] = useState(null);
  const [email, setEmail] = useState(getProEmail() || "");
  const [submitting, setSubmitting] = useState(false);
  const sub = useSubscription();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlan((r.data.plans || [])[0])).catch(() => {});
  }, []);

  const onUpgrade = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Pookie, drop a real email so we can send your receipt.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/checkout/order", {
        plan_id: plan?.id || "pookie-pro-monthly",
        email,
        device_id: getDeviceId(),
      });
      setProEmail(email);

      await openRazorpay(order, {
        email,
        contact: user?.phone || "",
        onSuccess: async (resp) => {
          try {
            await api.post("/checkout/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Welcome to Pookie Pro!");
            sub.refresh && sub.refresh();
            nav(`/pay/success?order_id=${resp.razorpay_order_id}`);
          } catch (e) {
            toast.error("Payment verification failed. Contact support.");
          } finally {
            setSubmitting(false);
          }
        },
        onDismiss: (err) => {
          setSubmitting(false);
          if (err) toast.error(err.description || "Payment cancelled.");
        },
      });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || e?.message || "Could not start checkout.");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-12" data-testid="pricing-page">
      <header className="text-center max-w-3xl mx-auto mb-12">
        <div className="pl-chip bg-[#FFE600] mx-auto inline-flex mb-4">
          <Sparkles size={14} strokeWidth={3} /> Pricing
        </div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95]">
          Justice should be <span className="bg-[#B4F8C8] px-2 border-[3px] border-black inline-block rotate-[-1deg]">cheap.</span>
        </h1>
        <p className="font-mono-pl text-sm md:text-base mt-5 text-black/75">
          Start with 2 free verdicts. When the drama gets serious, Pookie Pro keeps Judge Pookie on the clock — for less than a chai a day.
        </p>
      </header>

      <div className="grid md:grid-cols-2 gap-6 md:gap-8">
        {/* Free */}
        <div className="pl-card bg-white p-7 md:p-9" data-testid="plan-free">
          <div className="pl-label">Public Defender</div>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="font-display text-6xl">₹0</span>
            <span className="font-mono-pl text-sm">forever</span>
          </div>
          <p className="font-mono-pl text-sm mt-3 text-black/70">Try the court. No card needed.</p>
          <ul className="mt-6 space-y-2 font-mono-pl text-sm">
            {FREE_BULLETS.map((f) => (
              <li key={f} className="flex items-center gap-2">
                <Check size={14} strokeWidth={3} /> {f}
              </li>
            ))}
          </ul>
          <Link to="/file" className="pl-btn pl-btn-ghost mt-7 w-full justify-center" data-testid="plan-free-cta">
            Start filing
          </Link>
        </div>

        {/* Pro */}
        <div className="pl-card bg-[#FFAEBC] p-7 md:p-9 relative" data-testid="plan-pro">
          <div className="absolute -top-3 -right-3 pl-chip bg-[#FFE600] font-display text-xs">
            <Crown size={14} strokeWidth={3} /> Most popular
          </div>
          <div className="pl-label">Pookie Pro</div>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="font-display text-6xl">₹{plan?.amount || 99}</span>
            <span className="font-mono-pl text-sm">/ month</span>
          </div>
          <p className="font-mono-pl text-sm mt-3 text-black/80">Unlimited rulings for 30 days. Less than a chai a day.</p>
          <ul className="mt-6 space-y-2 font-mono-pl text-sm">
            {PRO_BULLETS.map((f) => (
              <li key={f} className="flex items-center gap-2">
                <Check size={14} strokeWidth={3} /> {f}
              </li>
            ))}
          </ul>

          {sub.is_pro ? (
            <div className="pl-card-flat bg-white mt-6 p-4 text-center">
              <div className="pl-chip bg-[#B4F8C8] inline-flex">
                <ShieldCheck size={14} strokeWidth={3} /> You're already Pro
              </div>
              <p className="font-mono-pl text-xs mt-2 text-black/70">
                Expires {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "—"}
              </p>
            </div>
          ) : (
            <>
              <label className="pl-label mt-6 block">Email for receipt</label>
              <input
                data-testid="pricing-email"
                className="pl-input mt-2"
                type="email"
                placeholder="pookie@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                data-testid="pricing-upgrade"
                onClick={onUpgrade}
                disabled={submitting}
                className="pl-btn pl-btn-primary w-full justify-center mt-4"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> Opening Razorpay…
                  </>
                ) : (
                  <>
                    <Sparkles size={16} strokeWidth={3} /> Pay ₹{plan?.amount || 99} with Razorpay
                  </>
                )}
              </button>
              <p className="font-mono-pl text-[11px] text-center text-black/60 mt-2">
                Secure Razorpay checkout. Test card: 4111 1111 1111 1111, any future date, any CVV.
              </p>
            </>
          )}
        </div>
      </div>

      <section className="mt-16 grid md:grid-cols-3 gap-5" data-testid="pricing-faq">
        {[
          ["Is this real legal advice?", "Absolutely not. Judge Pookie is comedic AI. Don't subpoena anyone with these verdicts."],
          ["What happens after 30 days?", "Your Pro access ends and you go back to 2 free verdicts. Renew anytime."],
          ["Can I cancel?", "There's nothing to cancel — it's a one-month pass. Just don't renew and you're done."],
        ].map(([q, a], i) => (
          <div key={q} className={`pl-card p-5 ${i % 2 === 0 ? "bg-[#A0E7E5]" : "bg-[#FFE600]"}`}>
            <div className="font-display text-lg">{q}</div>
            <p className="font-mono-pl text-sm mt-2 text-black/80">{a}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
